import {Component, inject, input, OnInit} from '@angular/core'
import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms'
import {MatButton, MatIconButton} from '@angular/material/button'
import {MatCheckbox} from '@angular/material/checkbox'
import {MatError, MatFormField, MatLabel, MatSuffix} from '@angular/material/form-field'
import {MatIcon} from '@angular/material/icon'
import {MatInput} from '@angular/material/input'
import {MatRadioButton, MatRadioGroup} from '@angular/material/radio'
import {MatOption, MatSelect} from '@angular/material/select'
import {getTakeUntilDestroyed, SpbAccessibleLinkDirective, SPBConstants} from '@sparbanken-syd/sparbanken-syd-theme'
import {IKycForm} from '@sparbanken-syd/spb-savings-account-backend'
import {OrientationService} from '../../../services/orientation.service'
import {FormUtils} from '../../../utils/form.utils'

export const workingAbroadOptions = [
  {value: 'workingAbroad', label: 'Arbetar utomlands'},
  {value: 'propertyAbroad', label: 'Fastighet eller lägenhet i utlandet'},
  {value: 'other', label: 'Annan'},
  {value: 'none', label: 'Ingen'}
]

@Component({
  selector: 'spb-tax-residency',
  templateUrl: './tax-residency.component.html',
  styleUrl: '../kyc.component.scss',
  imports: [ReactiveFormsModule, MatRadioGroup, MatRadioButton, MatCheckbox, MatFormField, MatLabel, MatSelect, MatOption, MatError, MatButton, MatInput, MatIcon, MatSuffix, SpbAccessibleLinkDirective, MatIconButton]
})
export class TaxResidencyComponent implements OnInit {
  public taxResidencyForm = input.required<any>()
  public kycForm = input.required<IKycForm>()

  public workingAbroadOptions = workingAbroadOptions

  protected readonly countryList = SPBConstants.CountryList
    .filter(country => country.name !== 'Sverige')

  protected readonly orientationService = inject(OrientationService)
  private readonly takeUntilDestroyed = getTakeUntilDestroyed()

  get formGroups(): FormGroup[] {
    return (this.taxResidencyForm()
      .get('taxResidencyInfo') as FormArray).controls as FormGroup[]
  }

  public ngOnInit() {
    this.taxResidencyForm().controls.taxResidencyNotSwedish.valueChanges
      .subscribe(
        (notSwedishTax: boolean) => {
          this.taxResidencyForm().controls.taxResidencyInfo.clear()
          if (notSwedishTax && this.checkIfUsaPerson()) {
            this.addControlsToFormGroup('USA')
          } else if (notSwedishTax) {
            this.addControlsToFormGroup()
          }
        }
      )
  }

  public onCheckboxChange(value: string, control: string, checked: boolean, formGroup: FormGroup) {
    // Update the form group's value based on the checkbox state
    formGroup.patchValue({[control]: checked ? value : null})

    // Determine if the 'other' option is selected and apply or remove validation accordingly
    const isOtherSelected = value === 'other'
    FormUtils.addOrRemoveValidation(isOtherSelected && checked, formGroup.controls.taxResidencyOtherRelation, [Validators.required])
  }

  public createControls(taxCountry?: string): FormGroup {
    const formGroup = new FormGroup({
      taxResidencyCountry: new FormControl(taxCountry, {
        validators: [Validators.required]
      }),
      taxResidencyTin: new FormControl(null, {
        validators: []
      }),
      taxResidencyRelation: new FormControl(null, {
        validators: [Validators.required]
      }),
      taxResidencyOtherRelation: new FormControl(null, {
        validators: []
      })
    })

    this.updateTinValidation(formGroup, taxCountry)

    formGroup.controls.taxResidencyCountry.valueChanges
      .pipe(this.takeUntilDestroyed())
      .subscribe((selectedCountry) => {
        this.updateTinValidation(formGroup, selectedCountry)
      })

    return formGroup
  }

  public addControlsToFormGroup(taxCountry?: string) {
    this.taxResidencyForm().get('taxResidencyInfo')
      .push(this.createControls(taxCountry))

    setTimeout(() => {
      // Get all mat-select elements by formcontrolname
      const selectElements: NodeListOf<HTMLElement> = document.querySelectorAll(
        'mat-select[formcontrolname="taxResidencyCountry"]'
      )

      // Select the last one in the list and focus on it (the newly added one)
      selectElements[selectElements.length - 1]?.focus()
    })
  }

  public removeControl(index: number) {
    const array = this.taxResidencyForm()
      .get('taxResidencyInfo') as FormArray
    array.removeAt(index)
  }

  public checkIfSwedenOnly(): boolean {
    const swedenOnly =
      this.taxResidencyForm().controls.taxResidencyNotSwedish.value === false &&
      this.kycForm().primaryResidenceForm.primaryResidence !== 'Sverige'

    if (swedenOnly) {
      FormUtils.addOrRemoveValidation(true,
        this.taxResidencyForm().controls.onlySwedenTaxCheckbox,
        [Validators.requiredTrue])
    } else {
      FormUtils.addOrRemoveValidation(false,
        this.taxResidencyForm().controls.onlySwedenTaxCheckbox,
        [Validators.requiredTrue])
    }
    return swedenOnly
  }

  public checkIfUsaPerson(): boolean {
    const usaPerson =
      (this.kycForm().primaryResidenceForm.primaryResidence === 'USA' ||
        this.kycForm().birthForm.birthCountry === 'USA' ||
        this.kycForm().birthForm.citizenship.includes('USA') ||
        this.kycForm().greenCardForm.greenCard)

    this.addUsaValidation(usaPerson)
    return usaPerson
  }

  private updateTinValidation(formGroup: FormGroup, country?: string | null) {
    const isUsa = country === 'USA'
    FormUtils.addOrRemoveValidation(isUsa, formGroup.controls.taxResidencyTin, [Validators.required])
  }

  private addUsaValidation(usaPerson: boolean) {
    if (usaPerson && this.taxResidencyForm().controls.taxResidencyNotSwedish.value === false) {
      FormUtils.addOrRemoveValidation(true,
        this.taxResidencyForm().controls.notUsaCitizenCheckbox,
        [Validators.requiredTrue])
    } else {
      FormUtils.addOrRemoveValidation(false,
        this.taxResidencyForm().controls.notUsaCitizenCheckbox,
        [Validators.requiredTrue])
    }
  }
}

